import {
  authenticationContext,
  TokenRequestChannel,
  uiActions
} from '@codametrix/ui-common';
import { Auth } from '../actions/action-types';
import { logout, setActiveUser } from '../actions/ui';
import { setIsActive } from './session-activity';
import { tokenRequestListener } from '../store/store';
import history from '../history';

const WORKITEM_PATH_PREFIX = '/cmx/quality-assessment/workItem/';

const isWorkitemDeepLink = (url: URL): boolean =>
  url.pathname.startsWith(WORKITEM_PATH_PREFIX);

const resolveContextForTenant = (ctx: any, urlTenantId: string | null) => {
  if (!urlTenantId || !Array.isArray(ctx.contexts)) return ctx;
  const matchingOrg = ctx.contexts.find((c: any) => c.tenantId === urlTenantId);
  if (!matchingOrg) return ctx;
  return {
    ...ctx,
    activeContextId: matchingOrg.organizationId,
    activeContext: matchingOrg
  };
};

/**
 * Called before fetchContexts. Captures jump data, attempts a BroadcastChannel
 * token request for workitem deep links, and seeds Redux with the donor tab's
 * auth + context so fetchContexts can short-circuit without a login redirect.
 *
 * Returns true if the BroadcastChannel path succeeded (caller should call
 * resolveDeepLinkServiceLine after fetchContexts resolves).
 */
export const prepareDeepLinkBoot = async (
  url: URL,
  dispatch: any
): Promise<boolean> => {
  if (!authenticationContext.getToken()) {
    const { saveJump } = await import('./app-jump');
    saveJump();
  }

  if (!isWorkitemDeepLink(url)) return false;
  if (globalThis.sessionStorage?.getItem('deepLinkBoot') !== '1') return false;

  const auth = await TokenRequestChannel.request(2000);
  if (!auth?.token) return false;

  authenticationContext.setAuthorization(auth);
  dispatch({ type: Auth.TOKEN_AVAILABLE, payload: auth });
  setIsActive(true);
  tokenRequestListener.startListening();

  if (auth.context) {
    const contextToApply = resolveContextForTenant(
      auth.context,
      url.searchParams.get('tenantId')
    );
    dispatch(uiActions.chooseContext(contextToApply));
  }

  if (auth.activeUser) {
    dispatch(setActiveUser(auth.activeUser as any));
  }

  return true;
};

/**
 * Called when fetchContexts rejects. Redirects to login with a message when
 * there is no authenticated peer tab and no existing session.
 */
export const handleBootstrapRejected = (dispatch: any): void => {
  if (globalThis.sessionStorage?.getItem('deepLinkBoot') !== '1') return;
  globalThis.sessionStorage.setItem(
    'deepLinkLoginMessage',
    'Please log in to view your case'
  );
  dispatch(logout());
  history.push('/');
};

/**
 * Called after fetchContexts resolves in the BroadcastChannel path. Sets the
 * service line from the jump data so the grading screen renders in the correct
 * context (the normal login flow does this; the BroadcastChannel path bypasses it).
 */
export const resolveDeepLinkServiceLine = async (
  ctx: CMx.Context,
  dispatch: any
): Promise<void> => {
  const { peekJump } = await import('./app-jump');
  const jumpInfo = peekJump();
  if (!ctx?.activeContext || jumpInfo.serviceLineId === null) return;
  const sl = ctx.activeContext.serviceLines.find(
    (s: any) => s.id === jumpInfo.serviceLineId
  );
  if (sl) {
    const { saveServiceLine } = await import('../actions/service-lines');
    dispatch(saveServiceLine(sl));
  }
};
